/**
 * UserDetailPage — route `/users/:userId`.
 *
 * SCRUM-108 Phase 2: real BE-driven detail for any org member, plus an
 * inline edit affordance on the Profile section. Removes the
 * "self-only" placeholder branch from PR #134.
 *
 * Status surface (sourced from `useOrgMemberDetail`):
 *   - 'loading'   → aria-busy hint.
 *   - 'ready'     → hero + Profile + Access + the four backend-pending
 *                   sections that wait on Phases 3/5/6.
 *   - 'forbidden' → permission-denied alert.
 *   - 'notFound'  → not-found alert.
 *   - 'error'     → generic alert + Retry.
 *
 * Edit affordance:
 *   - "Edit profile" button gated by
 *     `<RequirePermission slug="identity.users.profile.write">`.
 *   - Clicking flips local `isEditing` state; the read-only `<dl>` is
 *     replaced by `<ProfileEditForm>` seeded with the BE detail.
 *   - On submit success → flip back to read-only; the hook invalidates
 *     the detail query so a fresh GET re-seeds the view.
 *   - On 412 (`kind: 'stale'`) → keep the form open + show a stale
 *     banner that hints the user to reload.
 *   - On any other failure → keep the form open + generic error banner.
 */

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@sparta/ui-kit';
import { useOrgMemberDetail } from '../api/use-org-member-detail.js';
import { useUpdateOrgMemberProfile } from '../api/use-update-org-member-profile.js';
import { AnchorPills } from '../_components/AnchorPills.js';
import { AuditList } from '../_components/AuditList.js';
import { ProfileEditForm } from '../_components/ProfileEditForm.js';
import { SectionCard } from '../_components/SectionCard.js';
import { SectionHeading } from '../_components/SectionHeading.js';
import { SessionsList } from '../_components/SessionsList.js';
import { UserActionsMenu } from '../_components/UserActionsMenu.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';
import type { OrgMemberDetailView } from '../api/types.js';

interface SectionMeta {
  readonly id: string;
  readonly label: string;
  readonly count?: number;
}

const PROFILE_WRITE_SLUG = 'identity.users.profile.write' as const;

function statusLabel(
  status: OrgMemberDetailView['status'],
): 'Active' | 'Pending verification' | 'Suspended' {
  switch (status) {
    case 'active':
      return 'Active';
    case 'pending_verification':
      return 'Pending verification';
    case 'suspended':
      return 'Suspended';
  }
}

export function UserDetailPage(): React.ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const detail = useOrgMemberDetail(userId);

  return (
    <section
      className="flex flex-col gap-4"
      aria-labelledby="user-detail-back"
    >
      <Button
        id="user-detail-back"
        variant="ghost"
        size="sm"
        onClick={() => navigate('/users')}
        className="self-start"
      >
        ← Users
      </Button>

      {detail.status === 'loading' ? (
        <section
          aria-busy="true"
          aria-label="Loading user"
          className="pt-2 text-sm text-[var(--fg-tertiary)]"
        >
          Loading…
        </section>
      ) : null}

      {detail.status === 'forbidden' ? (
        <div
          role="alert"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          You don’t have permission to view this user. Ask an organization
          owner or admin if you need access.
        </div>
      ) : null}

      {detail.status === 'notFound' ? (
        <div
          role="alert"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          User not found. This member may have been removed from the
          organization, or the link is out of date.
        </div>
      ) : null}

      {detail.status === 'error' ? (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          <p>We couldn’t load this user. The backend may be unreachable.</p>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => detail.refetch()}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {detail.status === 'ready' ? (
        <ReadyDetail data={detail.data} etag={detail.etag} />
      ) : null}
    </section>
  );
}

interface ReadyDetailProps {
  readonly data: OrgMemberDetailView;
  readonly etag: string;
}

function ReadyDetail({ data, etag }: ReadyDetailProps): React.ReactElement {
  const update = useUpdateOrgMemberProfile();
  const [isEditing, setIsEditing] = React.useState(false);
  const [activeAnchor, setActiveAnchor] = React.useState<string>('profile');
  const [staleMessage, setStaleMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // The Properties section only makes sense for users who actually
  // hold the property-owner role — for every other role (members,
  // org-admins, group-admins, etc.) the property-ownership scope is
  // not part of their identity model. Gating the tab on the target
  // user's roles keeps the nav rail honest about what's on the page
  // (the section's render is gated below by the same predicate).
  const isPropertyOwner = data.roles.some(
    (role) => role.slug === 'property_owner',
  );

  const sections: ReadonlyArray<SectionMeta> = [
    { id: 'profile', label: 'Profile' },
    { id: 'access', label: 'Access', count: data.roles.length },
    ...(isPropertyOwner
      ? ([{ id: 'properties', label: 'Properties' }] as const)
      : []),
    { id: 'activity', label: 'Activity' },
    { id: 'sessions', label: 'Sessions' },
  ];

  function goToAnchor(id: string): void {
    setActiveAnchor(id);
    const el = document.getElementById(`anchor-${id}`);
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  async function handleSubmit(
    body: Parameters<typeof update.mutateAsync>[0]['body'],
  ): Promise<void> {
    setStaleMessage(null);
    setErrorMessage(null);
    try {
      await update.mutateAsync({ userId: data.userId, etag, body });
      setIsEditing(false);
    } catch (e: unknown) {
      const err = e as { kind?: string };
      if (err.kind === 'stale') {
        setStaleMessage(
          'This profile was updated by another admin. Reload the page to see the latest, then re-apply your changes.',
        );
      } else if (err.kind === 'unprocessable') {
        const message =
          typeof (e as { message?: unknown }).message === 'string'
            ? (e as { message: string }).message
            : 'Please review the highlighted fields.';
        setErrorMessage(message);
      } else if (err.kind === 'forbidden') {
        setErrorMessage(
          'You don’t have permission to update this profile. The page will refresh permissions on your next request.',
        );
      } else {
        setErrorMessage('We couldn’t save your changes. Try again in a moment.');
      }
    }
  }

  return (
    <>
      <section
        aria-label="Member identity"
        className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            {(() => {
              const nameParts = [data.firstName, data.lastName].filter(
                (p): p is string => typeof p === 'string' && p.length > 0,
              );
              const displayName =
                nameParts.length > 0 ? nameParts.join(' ') : data.email;
              const subtitle =
                nameParts.length > 0 ? data.email : null;
              return (
                <>
                  <h1 className="text-2xl font-semibold text-[var(--fg-primary)]">
                    {displayName}
                  </h1>
                  {subtitle !== null ? (
                    <p className="mt-1 text-sm text-[var(--fg-tertiary)]">
                      {subtitle}
                    </p>
                  ) : null}
                  <p className="mt-1 text-sm text-[var(--fg-secondary)]">
                    {data.roles[0]?.displayName ?? '(no role)'} ·{' '}
                    {statusLabel(data.status)}
                  </p>
                </>
              );
            })()}
          </div>
          <UserActionsMenu target={data} />
        </div>
      </section>

      <AnchorPills
        sections={sections}
        active={activeAnchor}
        onChange={goToAnchor}
        aria-label="User detail sections"
      />

      <section
        id="anchor-profile"
        aria-labelledby="profile-heading"
        className="scroll-mt-4"
      >
        <div className="flex items-center justify-between gap-3">
          <SectionHeading id="profile-heading">Profile</SectionHeading>
          {!isEditing ? (
            <RequirePermission slug={PROFILE_WRITE_SLUG}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStaleMessage(null);
                  setErrorMessage(null);
                  setIsEditing(true);
                }}
              >
                Edit profile
              </Button>
            </RequirePermission>
          ) : null}
        </div>
        <SectionCard>
          {isEditing ? (
            <ProfileEditForm
              initial={data}
              onSubmit={handleSubmit}
              onCancel={() => setIsEditing(false)}
              isPending={update.isPending}
              error={staleMessage ?? errorMessage ?? undefined}
            />
          ) : (
            <ReadOnlyProfile data={data} />
          )}
        </SectionCard>
      </section>

      <section
        id="anchor-access"
        aria-labelledby="access-heading"
        className="scroll-mt-4"
      >
        <SectionHeading id="access-heading">Access</SectionHeading>
        <SectionCard>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="text-[var(--fg-tertiary)]">Role</dt>
              <dd className="text-[var(--fg-primary)]">
                {data.roles[0]?.displayName ?? '(no role)'}
              </dd>
            </div>
          </dl>
        </SectionCard>
      </section>

      {isPropertyOwner ? (
        <section id="anchor-properties" className="scroll-mt-4">
          <SectionHeading>Properties</SectionHeading>
          <SectionCard>
            <p className="text-sm text-[var(--fg-secondary)]">
              Per-property access scoping is owned by the DS-007
              PropertyOwnership initiative, not by SCRUM-108. When that
              ships, this section will list the properties this user can
              see and act on.
            </p>
          </SectionCard>
        </section>
      ) : null}

      <section id="anchor-activity" className="scroll-mt-4">
        <SectionHeading>Activity</SectionHeading>
        <SectionCard>
          <RequirePermission
            slug="identity.users.audit.read"
            fallback={
              <p className="text-sm text-[var(--fg-secondary)]">
                You don’t have permission to view this user’s activity.
              </p>
            }
          >
            <AuditList userId={data.userId} />
          </RequirePermission>
        </SectionCard>
      </section>

      <section id="anchor-sessions" className="scroll-mt-4">
        <SectionHeading>Sessions</SectionHeading>
        <SectionCard>
          <RequirePermission
            slug="identity.users.sessions.read"
            fallback={
              <p className="text-sm text-[var(--fg-secondary)]">
                You don’t have permission to view this user’s active sessions.
              </p>
            }
          >
            <SessionsList userId={data.userId} />
          </RequirePermission>
        </SectionCard>
      </section>

    </>
  );
}

function ReadOnlyProfile({
  data,
}: {
  readonly data: OrgMemberDetailView;
}): React.ReactElement {
  return (
    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
      <div>
        <dt className="text-[var(--fg-tertiary)]">Email</dt>
        <dd className="text-[var(--fg-primary)]">{data.email}</dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Email verified</dt>
        <dd className="text-[var(--fg-primary)]">
          {data.emailVerifiedAt === null
            ? 'Not yet verified'
            : new Date(data.emailVerifiedAt).toLocaleDateString()}
        </dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Job title</dt>
        <dd className="text-[var(--fg-primary)]">
          {data.jobTitle ?? '—'}
        </dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Department</dt>
        <dd className="text-[var(--fg-primary)]">
          {data.department ?? '—'}
        </dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Phone</dt>
        <dd className="text-[var(--fg-primary)]">{data.phone ?? '—'}</dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Time zone</dt>
        <dd className="text-[var(--fg-primary)]">{data.timeZone}</dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Language</dt>
        <dd className="text-[var(--fg-primary)]">{data.language}</dd>
      </div>
      <div>
        <dt className="text-[var(--fg-tertiary)]">Country</dt>
        <dd className="text-[var(--fg-primary)]">{data.country ?? '—'}</dd>
      </div>
    </dl>
  );
}
